import { FC } from 'react';
import { Route, Redirect, RouteComponentProps } from 'react-router-dom';
import { usePermissionsContext } from 'store/permissions';
import { useTwoFactorContext } from 'store/two-factor';
import { getAccessToken, getUserInfo } from 'utils/auth';
import { PermissionKeys } from 'store/types';

interface IProps {
  path: string;
  isExact: boolean;
  is2FaProtected?: boolean;
  permissionGrantingAccess?: string;
  render: (props: RouteComponentProps) => JSX.Element;
}

/**
 * A wrapper for <Route> that redirects to the
 * login screen if user is not yet logged in.
 *
 * Passing state prop `from` should serve for
 * further redirect once the user is logged in,
 * so we can send him to the page he initially
 * wanted to go to (if he can access it).
 *
 * Private route could as well be 2FA protected
 * which means that in case 2FA flow is not complete
 * this route shouldn't be rendered at all
 * and should redirect to 2FA activate screen instead
 *
 * !!! 2FA is complete if 2FA flow is disabled OR user has set 2FA !!!
 */
const PrivateRoute: FC<IProps> = ({ path, isExact, is2FaProtected, permissionGrantingAccess, render }: IProps) => {
  const isUserLoggedIn = getAccessToken();
  const { is2FaComplete } = useTwoFactorContext();
  const { checkPermission } = usePermissionsContext();

  const userInfo = getUserInfo();

  // Enhanced permission checking
  const hasAccess = () => {
    // First check if user is a candidate and should be restricted
    if (userInfo.isCandidate) {
      const candidateAllowedPaths = [
        '/home',
        '/my-documents',
        '/documents-wizard',
        '/document-activity',
        '/multiple-document-activity',
        '/training-materials',
        '/profile/2fa-activate',
      ];

      const isAllowed = candidateAllowedPaths.some(allowedPath => path.startsWith(allowedPath));

      if (!isAllowed) return false;
    }

    // Then check permissions if they exist
    if (!permissionGrantingAccess) return true;

    // Handle compound permissions for specific routes that need both employer nav AND specific permissions
    if (path === '/reports/leaves-form-76') {
      return checkPermission(PermissionKeys.CAN_SEE_EMPLOYER_NAV) && checkPermission(PermissionKeys.CAN_MANAGE_LEAVES);
    }
    if (path === '/reports/hourly-absences') {
      return (
        checkPermission(PermissionKeys.CAN_SEE_EMPLOYER_NAV) &&
        checkPermission(PermissionKeys.CAN_MANAGE_HOURLY_ABSENCES)
      );
    }
    if (path === '/reports/health-and-safety-training') {
      return (
        checkPermission(PermissionKeys.CAN_SEE_EMPLOYER_NAV) &&
        checkPermission(PermissionKeys.CAN_MANAGE_HEALTH_AND_SAFETY_TRAINING)
      );
    }
    if (path === '/reports/work-trips') {
      return (
        checkPermission(PermissionKeys.CAN_SEE_EMPLOYER_NAV) && checkPermission(PermissionKeys.CAN_MANAGE_WORK_TRIPS)
      );
    }
    if (path === '/structure/overtimes') {
      return checkPermission(PermissionKeys.CAN_SEE_EMPLOYER_NAV) && checkPermission(PermissionKeys.CAN_MANAGE_LEAVES);
    }

    // For all other routes, use standard permission checking
    return checkPermission(permissionGrantingAccess);
  };

  return (
    <Route
      path={path}
      exact={isExact}
      render={props => {
        if (isUserLoggedIn) {
          if (is2FaProtected && !is2FaComplete) {
            return <Redirect to="/profile/2fa-activate" />;
          }
          if (!hasAccess()) {
            return <Redirect to="/home" />;
          }
          return render(props);
        }
        return (
          <Redirect
            to={{
              pathname: '/',
              state: {
                from: props.location,
                email: userInfo.email,
              },
            }}
          />
        );
      }}
    />
  );
};

export default PrivateRoute;
